<template>
  <div>
    <div class="header" v-if="graduate.id">
      <div class="container-fluid container-boxed">
        <div class="header-body">
          <div class="row">
            <div class="col d-flex align-items-center">
              <div>
                <h6 class="header-pretitle">Graduate</h6>
                <h1 class="header-title">{{ graduate.name }}</h1>
              </div>
            </div>

            <div class="col-auto d-flex align-items-center">
              <div v-if="graduateIsEnabled && $userIsAdmin" class="badge badge-success">Account Enabled</div>
              <div v-else-if="graduateIsDisabled" class="badge badge-danger">Account Disabled</div>
            </div>
          </div>
        </div>
      </div>
    </div><!-- / .header -->

    <div v-if="graduate.id" class="container-fluid container-boxed">
      <div class="card">
        <div class="card-body">
          <h2>{{ graduate.program.name }}</h2>

          <div class="row">
            <div class="col-6 col-lg-6 col-xl-4">
              <p>
                <strong>Current Placement</strong>
                <br>{{ currentPlacementAgency | nullable }}
              </p>
            </div>

            <div class="col-6 col-lg-6 col-xl-4">
              <p>
                <strong>Home Agency</strong>
                <br>{{ homeAgency | nullable }}
              </p>
            </div>
          </div>

          <div class="row">
            <div class="col-6 col-lg-6 col-xl-4">
              <p>
                <strong>Current Manager</strong>
                <br>
                <template v-if="currentPlacementManager">
                  <span class="mr-4">{{ currentPlacementManager.name }}</span>
                  <a :href="`mailto:${currentPlacementManager.email}`" class="mr-4 text-muted">{{ currentPlacementManager.email }}</a>
                  <a :href="`tel:${currentPlacementManager.phone}`" class="text-muted">{{ currentPlacementManager.phone }}</a>
                </template>
                <span v-else class="mr-4">Manager undefined</span>
              </p>
            </div>

            <div class="col-6 col-lg-6 col-xl-4">
              <p>
                <strong>Candidate ID</strong>
                <br>{{ graduate.candidate_id | nullable }}
              </p>
            </div>
          </div>

          <h3 class="mt-5">Contact Details</h3>

          <div class="row">
            <div class="col-12">
              <p>
                <strong>Email Address</strong>
                <br>
                <a :href="`mailto:${activePlacementEmail}`">{{ activePlacementEmail }}</a>
              </p>
            </div>
            <div class="col-6 col-lg-6 col-xl-4">
              <p>
                <strong>Phone</strong>
                <br>
                <a :href="`tel:${graduate.phone}`">{{ graduate.phone | nullable }}</a>
              </p>
            </div>
          </div><!-- /.row -->

          <document-upload/>

          <div class="row mt-3">
            <div class="col">
              <h3>
                Initial Capability Data:
                <font-awesome v-if="graduate.initial_capabilities_set" :icon="['far', 'check']"> Set</font-awesome>
                <font-awesome v-else :icon="['far', 'times']"> Not Set</font-awesome>
              </h3>
            </div>
            <div class="col-auto" v-if="$userIsAdmin">
              <router-link :to="{ name: 'graduates.capabilities' }" class="btn btn-sm btn-outline-info">
                <font-awesome :icon="['far', 'pencil']" class="mr-2"/>
                Edit
                <span class="sr-only">initial capability data</span>
              </router-link>
            </div>
          </div><!-- /.row -->
        </div>
      </div><!-- /.card -->

      <div class="decorative-divider"></div>

      <div class="card">
        <ul class="list-data no-hover has-button">
          <li class="data-heading d-none d-md-block">
            <div class="row">
              <div class="col">Placements and Performance Plans</div>
              <div class="col-auto text-right">Placement Dates</div>
            </div><!-- /.row -->
          </li>

          <graduates-single-placements-list-item v-for="placement in graduate.placements" :placement="placement" :key="placement.graduate_placement_id" @click="setActivePlacement"/>
          <template v-if="$userIsManager || $userIsCoordinator">
            <li class="data-heading d-none d-md-block mt-4">
              <div class="row">
                <router-link class="col" :to="{ name: 'graduates.assessments.single', params: { graduateId: graduate.id } }" v-if="$userIsCoordinator">
                  Overall Performance Assessment
                </router-link>
                <div class="col" v-else>Overall Performance Assessment</div>
              </div><!-- /.row -->
            </li>

            <li>
              <router-link :to="{ name: 'graduates.assessments.single', params: { graduateId: graduate.id } }">
                <p style="color: #646974; font-size: 0.875rem; font-weight: bold" class="mb-3">
                  Final placement managers are responsible for completing the overall performance assessment,
                  which evaluates the graduate’s performance across the entire 18-month program. This assessment
                  should be completed after the final placement review. The overall performance assessment rating
                  determines if the graduate is eligible for a promotion under rule 20A of the Government Sector
                  Employment (General) Rules 2014. Graduates rated as ‘exceeding’ and who meet the other program
                  requirements may be considered for a fast-track promotion. However, an ‘exceeding’ rating does
                  not guarantee a promotion—it depends on the agency’s availability of an appropriate higher-level role.
                </p>

                <div class="row align-items-center mb-md-4">
                  <div class="col d-md-none">
                    <p class="strong mb-0">Overall Performance Assessment</p>
                  </div>
                  <div class="col-auto">
                    <div class="row">
                      <p class="col-6 text-muted">
                        <span class="data-label">Open Dates:</span>
                        {{ graduate.program ? graduate.program.open_date_formatted : null |nullable }}
                      </p>
                      <p class="col-6 text-muted">
                        <span class="data-label">Due Dates:</span>
                        {{ graduate.program ? graduate.program.due_date_formatted : null |nullable }}
                      </p>
                    </div>
                  </div>
                </div>

                <ul class="details">
                  <li style="float: none;"><strong>Department:</strong> {{ lastPlacement.agency ? lastPlacement.agency.name : 'Unassigned' }} </li>
                  <li><strong>Rating:</strong> {{ graduate.assessment ? graduate.assessment.rating_title : '-' }}</li>
                </ul><!-- /.details -->
              </router-link>
            </li>
          </template>
        </ul><!-- /.list-data -->
      </div><!-- /.card -->

      <template v-if="$userIsAdmin">
        <div class="card">
          <ul class="list-data no-hover has-button">
            <li class="data-heading d-none d-md-block">
              <div class="row">
                <router-link class="col" :to="{ name: 'graduates.assessments.single', params: { graduateId: graduate.id } }">
                  Overall Performance Assessment
                </router-link>
              </div>
            </li>

            <li>
              <a data-toggle="modal" data-target=".modal-assessment">
                <div class="row align-items-center details">
                  <div class="col">
                    <ul class="details">
                      <li>Department: <span v-if="currentPlacement && currentPlacement.agency">{{ currentPlacement.agency.name }}</span>
                        <span v-else>Unassigned</span>
                      </li>
                      <li>Manager: <span v-if="currentPlacementManager">{{ currentPlacementManager.name }}</span>
                        <span v-else>Unassigned</span>
                      </li>
                      <li></li>
                    </ul>
                  </div>
                  <div class="col-auto">
                    <p class="text-muted">
                      <span class="data-label d-lg-none">Dates:</span>
                      {{ graduate.assessment.open_date ? formatDate(graduate.assessment.open_date) : (graduate.program.open_date ? formatDate(graduate.program.open_date) : 'DD/MM/YYYY') }} to
                      {{ graduate.assessment.due_date ? formatDate(graduate.assessment.due_date) : (graduate.program.due_date ? formatDate(graduate.program.due_date) : 'DD/MM/YYYY') }}
                    </p>
                  </div>
                </div>
                <button type="button" class="btn text-muted">
                  <font-awesome :icon="['fal', 'cog']"/>
                  <span class="sr-only">Edit Overall Performance Assessment</span>
                </button>
              </a>
            </li>
          </ul>
        </div>
      </template>

      <div class="decorative-divider"></div>
      <LogCard v-if="$userIsAdmin" type="graduate" />
    </div><!-- /.container-fluid -->
    <portal to="modals">
      <placement-assignment-modal :placement="activePlacement" @closed="activePlacement = null"/>
    </portal>

    <LogDrawer v-if="$userIsAdmin" type="graduate" :target="graduate"/>
    <PerformanceAssessmentDrawer v-if="$userIsAdmin" :graduate="graduate"/>
  </div>
</template>

<script>
import { graduateComputed, graduateMethods } from 'vx/helpers'
import { helpers } from 'mixins/graduates'
import { LogCard, LogDrawer } from 'components/log'
import GraduatesSinglePlacementsListItem from './GraduatesSinglePlacementsListItem'
import DocumentUpload from './GraduatesSingleDocumentUpload'
import PlacementAssignmentModal from './GraduatePlacementAssignmentModal'
import moment from 'moment'
import PerformanceAssessmentDrawer from '../../components/graduates/PerformanceAssessmentDrawer.vue'

export default {
  mixins: [helpers],

  props: {
    id: {
      required: false,
      type: Number,
      default: null
    }
  },

  components: {
    PerformanceAssessmentDrawer,
    LogCard,
    LogDrawer,
    GraduatesSinglePlacementsListItem,
    DocumentUpload,
    PlacementAssignmentModal
  },

  data () {
    return {
      activePlacement: {},
      today: moment()
    }
  },

  computed: {
    ...graduateComputed,

    /**
     * https://support.codium.com.au/a/tickets/12188
     *
     * display the current placement email address
     * instead of backup email address
     *
     * @return string
     */
    activePlacementEmail () {
      if (
        !this.graduate.placements ||
        !Array.isArray(this.graduate.placements) ||
        (Array.isArray(this.graduate.placements) && this.graduate.placements.length === 0)
      ) {
        return this.graduate.email
      }

      const activePlacements = this.graduate.placements.filter(
        placement => this.today.isBefore(placement.end_date) && this.today.isAfter(placement.start_date)
      )

      return (activePlacements.length > 0 && activePlacements[activePlacements.length - 1].email)
        ? activePlacements[activePlacements.length - 1].email
        : (this.graduate.email != null ? this.graduate.email : 'Unassigned')
    },
    lastPlacement () {
      if (!this.graduate.placements.length) return null

      return [...this.graduate.placements]
        .sort((a, b) => b.number - a.number)[0]
    },
    lastPlacementFinalised () {
      return this.lastPlacement ? this.lastPlacement.finalised : false
    }
  },

  async created () {
    await this.getGraduate(this.id)
  },

  methods: {
    ...graduateMethods,

    setActivePlacement (placement) {
      this.activePlacement = placement
    },

    formatDate (date) {
      return new Date(date).toLocaleDateString('en-GB')
    }
  }
}
</script>
